<script setup>
import { computed } from 'vue';

// Render simples e robusto de um email DENTRO de um card de thread.
// Versao reescrita: sem <style scoped>, sem componentes filhos, html direto.
// Garante que SEMPRE aparece algo visivel — pior caso, "(sem conteudo)".

const props = defineProps({
  message: { type: Object, required: true },
});

const isOutgoing = computed(() => {
  const t = props.message.messageType;
  return t === 1 || t === 3;
});

// Cobre snake_case (caso useCamelCase nao tenha rodado deep em alguma
// rota) e camelCase. Pega o primeiro que tiver conteudo.
const pickEmailHtml = m => {
  const ca = m.contentAttributes || m.content_attributes || {};
  const e = ca.email || {};
  return (
    e.htmlContent?.full ||
    e.htmlContent?.reply ||
    e.html_content?.full ||
    e.html_content?.reply ||
    ''
  );
};

const pickEmailText = m => {
  const ca = m.contentAttributes || m.content_attributes || {};
  const e = ca.email || {};
  return (
    e.textContent?.full ||
    e.textContent?.reply ||
    e.text_content?.full ||
    e.text_content?.reply ||
    ''
  );
};

const bodyHtml = computed(() => pickEmailHtml(props.message));
const bodyText = computed(() => pickEmailText(props.message));
const fallbackText = computed(() => props.message.content || '');

const hasAnyBody = computed(
  () => bodyHtml.value || bodyText.value || fallbackText.value
);

const fromEmail = computed(() => {
  const ca =
    props.message.contentAttributes || props.message.content_attributes || {};
  return ca.email?.from?.[0] || props.message.sender?.email || '';
});

const formatTime = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Aceita Unix epoch (number) ou ISO8601 (string) — openedAt vem como ISO do
// backend (Time.current.iso8601), createdAt vem como Unix epoch.
const formatDateTime = value => {
  if (!value) return '';
  const d =
    typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Status de leitura (so faz sentido pra outgoing — incoming nao tem pixel).
// Backend grava em content_attributes.email.{opened_at,last_opened_at,opens_count}.
const openInfo = computed(() => {
  if (!isOutgoing.value) return null;
  const ca =
    props.message.contentAttributes || props.message.content_attributes || {};
  const e = ca.email || {};
  const openedAt = e.openedAt || e.opened_at || null;
  const lastOpenedAt = e.lastOpenedAt || e.last_opened_at || null;
  const opensCount = Number(e.opensCount ?? e.opens_count ?? 0);
  return {
    opened: Boolean(openedAt),
    openedAt,
    lastOpenedAt: lastOpenedAt || openedAt,
    opensCount,
  };
});

const labels = {
  sent: 'Enviado',
  received: 'Recebido',
  noContent: '(sem conteúdo)',
  notRead: 'Não lido',
  readAt: 'Lido em',
};
</script>

<template>
  <div
    class="email-msg my-3 rounded-lg border p-3 min-h-[60px]"
    :class="
      isOutgoing
        ? 'bg-n-teal-2 border-n-teal-6'
        : 'bg-n-background border-n-weak'
    "
  >
    <div
      class="flex items-center gap-2 text-xs font-medium mb-2"
      :class="isOutgoing ? 'text-n-teal-11' : 'text-n-blue-11'"
    >
      <span
        :class="
          isOutgoing ? 'i-lucide-arrow-up-right' : 'i-lucide-arrow-down-left'
        "
        class="size-3"
      />
      <span>{{ isOutgoing ? labels.sent : labels.received }}</span>
      <span class="text-n-slate-10 font-normal">
        · {{ formatTime(message.createdAt || message.created_at) }}
      </span>
      <span v-if="fromEmail" class="text-n-slate-10 font-normal truncate">
        · {{ fromEmail }}
      </span>
      <span
        v-if="openInfo && openInfo.opened"
        class="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11 font-normal whitespace-nowrap"
        :title="
          openInfo.opensCount > 1
            ? `${openInfo.opensCount} aberturas — última em ${formatDateTime(openInfo.lastOpenedAt)}`
            : `Aberto em ${formatDateTime(openInfo.openedAt)}`
        "
      >
        <span class="i-lucide-eye size-3" />
        {{ labels.readAt }} {{ formatDateTime(openInfo.lastOpenedAt) }}
      </span>
      <span
        v-else-if="openInfo"
        class="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-10 font-normal whitespace-nowrap"
      >
        <span class="i-lucide-eye-off size-3" />
        {{ labels.notRead }}
      </span>
    </div>

    <div
      v-if="bodyHtml"
      class="text-sm text-n-slate-12 leading-relaxed prose prose-sm max-w-none"
      v-html="bodyHtml"
    />
    <div
      v-else-if="bodyText"
      class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed"
    >
      {{ bodyText }}
    </div>
    <div
      v-else-if="fallbackText"
      class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed"
    >
      {{ fallbackText }}
    </div>
    <div v-if="!hasAnyBody" class="text-xs text-n-slate-10 italic">
      {{ labels.noContent }}
    </div>
  </div>
</template>
